import { useEffect, useState } from 'react';
import { Headphones, FileText } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { ErrorState, LoadingState } from '../../components/LoadingState';
import { useGenerateAudioResume, useGenerateTextResume } from '../../api/queries';
import { useAppParams } from '../../api/params';
import { getFileUrl } from '../../api/client';
import styles from './SummaryView.module.css';

type Mode = 'text' | 'audio';

export function SummaryView() {
  const [mode, setMode] = useState<Mode>('text');
  const textMut = useGenerateTextResume();
  const audioMut = useGenerateAudioResume();

  useEffect(() => {
    if (mode === 'text' && !textMut.data && !textMut.isPending && !textMut.error) {
      textMut.mutate({ markdown: true });
    }
    if (mode === 'audio' && !audioMut.data && !audioMut.isPending && !audioMut.error) {
      audioMut.mutate({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  return (
    <div className={styles.view}>
      <div className={styles.tabs} role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'text'}
          className={mode === 'text' ? styles.tabActive : styles.tab}
          onClick={() => setMode('text')}
        >
          <FileText size={14} strokeWidth={2.2} /> Texto
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'audio'}
          className={mode === 'audio' ? styles.tabActive : styles.tab}
          onClick={() => setMode('audio')}
        >
          <Headphones size={14} strokeWidth={2.2} /> Audio
        </button>
      </div>

      <div className={styles.body}>
        {mode === 'text' ? <TextSummary mut={textMut} /> : <AudioSummary mut={audioMut} />}
      </div>
    </div>
  );
}

type Point = string | { title?: string; details?: string };
const renderPoint = (p: Point, key: number) => {
  if (typeof p === 'string') return <li key={key}>{p}</li>;
  return (
    <li key={key}>
      {p.title && <strong>{p.title}</strong>}
      {p.title && p.details ? <br /> : null}
      {p.details}
    </li>
  );
};

function TextSummary({ mut }: { mut: ReturnType<typeof useGenerateTextResume> }) {
  if (mut.isPending) return <LoadingState label="Resumiendo el módulo…" />;
  if (mut.error) return <ErrorState error={mut.error} onRetry={() => mut.mutate({ markdown: true })} />;
  const data = mut.data as any;
  if (!data) return null;
  const c = data.content ?? {};
  const md: string = c.markdown ?? c.summary ?? '';
  const main: Point[] = Array.isArray(c.main_points) ? c.main_points : [];
  const conclusions = c.conclusions;
  const conclusionsList: Point[] = Array.isArray(conclusions) ? conclusions : [];
  const conclusionsText: string = typeof conclusions === 'string' ? conclusions : '';

  return (
    <article className={styles.markdown}>
      {data.title && <h2 className={styles.title}>{data.title}</h2>}
      {data.description && <p className={styles.subtitle}>{data.description}</p>}
      <ReactMarkdown>{md}</ReactMarkdown>
      {main.length > 0 && (
        <section className={styles.section}>
          <h3>Ideas clave</h3>
          <ul className={styles.bullets}>{main.map((p, i) => renderPoint(p, i))}</ul>
        </section>
      )}
      {(conclusionsText || conclusionsList.length > 0) && (
        <section className={styles.section}>
          <h3>Conclusiones</h3>
          {conclusionsText ? (
            <ReactMarkdown>{conclusionsText}</ReactMarkdown>
          ) : (
            <ul className={styles.bullets}>{conclusionsList.map((p, i) => renderPoint(p, i))}</ul>
          )}
        </section>
      )}
    </article>
  );
}

function AudioSummary({ mut }: { mut: ReturnType<typeof useGenerateAudioResume> }) {
  const params = useAppParams();
  if (mut.isPending) return <LoadingState label="Generando audio…" hint="Sintetizando voz, espera unos segundos" />;
  if (mut.error) return <ErrorState error={mut.error} onRetry={() => mut.mutate({})} />;
  const data = mut.data as any;
  if (!data) return null;
  const file: string = data.content?.file ?? '';
  const summary: string = data.content?.summary ?? '';
  const src = getFileUrl(file, params);

  return (
    <div className={styles.audioWrap}>
      <audio className={styles.audio} controls src={src} preload="none" />
      {summary && (
        <details className={styles.transcript}>
          <summary>Ver transcripción</summary>
          <ReactMarkdown>{summary}</ReactMarkdown>
        </details>
      )}
    </div>
  );
}
